import React, { useEffect, useRef, useState } from 'react';
import TableColumn from './TableColumn';
import noData from '../../../../assets/img/svg/table/no-data-available.svg';
import dataFailed from '../../../../assets/img/svg/table/load-data-failed.svg';
import { t } from 'i18next';
import { useColumns } from '../../../../hook/useColumns';

import { cashRegisterTableColumn } from '../../../../enum/TableColumn';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAppSelector } from '../../../../hook/useAppSelector';
import store from '../../../../store/store';
import { useRowDensity } from '../../../../hook/useRowDensity';
import CustomScrollWrapper from '../CustomScrollWrapper';
import { setTableRange } from '../../../../store/slice/config/config';
import { useAppDispatch } from '../../../../hook/useAppDispatch';
import { useTableRange } from '../../../../hook/useTableRange';
import { useTableSort } from '../../../../hook/useTableSort';
import { HEADER_HEIGHT } from '../../../../constant/constants';

interface TableProps {
  data: any[];
  showFooter: boolean;
  error?: {} | null;
  className?: string;
  loaderIsShown?: boolean;
  dataLength: number;
}

const Table: React.FC<TableProps> = ({ data, showFooter, className, error, loaderIsShown, dataLength }) => {
  const dispatch = useAppDispatch();

  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  // Hooks for pinned/unpinned + order
  const { lockedColumns, pinnedColumns, unpinnedColumns } = useColumns();
  const searchInput = useAppSelector((state) => state.deviceList.searchInput);
  const { rowDensityHeight } = useRowDensity();
  const { sortedData, toggleSort, sortConfig } = useTableSort(data);

  const hasError = error;
  const hasNoData = !hasError && dataLength === 0;
  const hasData = !hasError && dataLength > 0;

  useEffect(() => {
    const container = parentRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [data, searchInput]);

  const getNoDataMessage = (): string => {
    if (error) {
      return t('general.dataLoadFailed');
    }

    return t('statisticPage.table.noData');
  };

  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    let timeoutId: number | null = null;

    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = window.setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;

        const dynamicStop = store.getState().stats.shouldViewPerCurrency
          ? store.getState().stats.locationColumnHeight
          : 0;

        const maxScroll = scrollHeight - clientHeight - dynamicStop;

        if (scrollTop > maxScroll) {
          container.scrollTo({
            top: maxScroll,
            behavior: 'smooth',
          });
        }
      }, 350);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    // Total number of items to virtualize.
    count: data.length,

    // Returns the scrollable container element.
    getScrollElement: () => parentRef.current,

    //Estimated row height.
    estimateSize: (index) => rowDensityHeight,

    // Number of extra items rendered above and below the viewport.
    overscan: 15,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowDensityHeight, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();
  const visibleRowsHeight = virtualRows[virtualRows.length - 1]?.end ?? 0;

  useTableRange({
    parentRef,
    rowDensityHeight,
    dataLength,
    onChange: (range) => {
      dispatch(setTableRange(range));
    },
  });

  return (
    <div
      className={`table_container ${className}`}
      style={{
        ['--table-content-height' as string]: `${visibleRowsHeight}px`,
        ['--table-header-height' as string]: `${HEADER_HEIGHT}px`,
      }}
    >
      <CustomScrollWrapper scrollRef={parentRef} rowVirtualizer={rowVirtualizer}>
        <div
          ref={parentRef}
          style={{
            height: '100%',
            overflowY: 'auto',
            position: 'relative',
            scrollBehavior: 'auto',
          }}
          className="virtual_table_container"
        >
          <div
            style={{
              height: error ? '100%' : totalHeight,
              position: 'relative',
              width: '100%',
            }}
            className="virtual_table virtual"
          >
            <div className="table_wrapper">
              <div className="table">
                {/* ERROR takes priority */}
                {!loaderIsShown && hasError && (
                  <div className="no_data">
                    <img className="no_data_img" src={dataFailed} alt={getNoDataMessage()} />
                    <p className="no_data_message">{getNoDataMessage()}</p>
                  </div>
                )}

                {/* Show NO DATA AVAILABLE only if there is no error */}
                {!loaderIsShown && hasNoData && (
                  <div className="no_data">
                    <img className="no_data_img" src={noData} alt={getNoDataMessage()} />
                    <p className="no_data_message">{getNoDataMessage()}</p>
                  </div>
                )}
                {hasData && (
                  <>
                    {/* Locked columns (Geo / CCY) */}
                    {lockedColumns.map((col, i) => (
                      <TableColumn
                        column={col}
                        data={sortedData}
                        showFooter={showFooter}
                        selectedRow={selectedRow}
                        onRowSelect={setSelectedRow}
                        virtualRows={virtualRows}
                        key={`locked_${col.name}`}
                        isFirstColumnAfterCCY={false}
                        isLastColumn={false}
                        className={col.name === cashRegisterTableColumn.ACTIONS ? 'table_image' : 'text'}
                        totalHeight={totalHeight}
                        toggleSort={toggleSort}
                        sortConfig={sortConfig}
                        visibleRowsHeight={visibleRowsHeight}
                      />
                    ))}

                    {/* PINNED COLUMNS */}
                    {pinnedColumns
                      .filter((col) => col.isVisible)
                      .map((col, i) => (
                        <TableColumn
                          column={col}
                          data={sortedData}
                          showFooter={showFooter}
                          selectedRow={selectedRow}
                          onRowSelect={setSelectedRow}
                          virtualRows={virtualRows}
                          key={`pinned_${col.name}`}
                          isFirstColumnAfterCCY={i === 0}
                          isLastColumn={i + 1 === pinnedColumns.length}
                          className={col.name === cashRegisterTableColumn.ACTIONS ? 'table_image' : 'number'}
                          totalHeight={totalHeight}
                          toggleSort={toggleSort}
                          sortConfig={sortConfig}
                          visibleRowsHeight={visibleRowsHeight}
                        />
                      ))}

                    {/* UNPINNED COLUMNS */}
                    {unpinnedColumns
                      .filter((col) => col.isVisible)
                      .map((col, i) => (
                        <TableColumn
                          column={col}
                          data={sortedData}
                          showFooter={showFooter}
                          selectedRow={selectedRow}
                          onRowSelect={setSelectedRow}
                          virtualRows={virtualRows}
                          key={`unpinned_${col.name}`}
                          isFirstColumnAfterCCY={i === 0}
                          isLastColumn={i + 1 === unpinnedColumns.length}
                          className={col.name === cashRegisterTableColumn.ACTIONS ? 'table_image' : 'number'}
                          totalHeight={totalHeight}
                          sortConfig={sortConfig}
                          toggleSort={toggleSort}
                          visibleRowsHeight={visibleRowsHeight}
                        />
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CustomScrollWrapper>
    </div>
  );
};

export default Table;
